import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Card,
  CardMedia,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  PhotoCamera,
  Add,
  Close,
  CheckCircle,
  ErrorOutline,
  Refresh,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface CarouselItem {
  id: string;
  preview: string;
  file: File;
  status: 'uploading' | 'success' | 'error';
}

interface ImageUploadProps {
  onProfileImageChange: (file: File | null) => void;
  onCarouselImagesChange: (files: File[]) => void;
  onRemoveProfileImage?: () => void;
  onRemoveCarouselImage?: (imageUrl: string) => void;
  onAutoUploadProfileImage?: (file: File) => Promise<boolean>;
  onAutoUploadCarouselImage?: (file: File) => Promise<boolean>;
  profileImage?: string;
  carouselImages?: string[];
}

const StatusOverlay: React.FC<{ status: 'uploading' | 'success' | 'error' }> = ({ status }) => (
  <Box sx={{
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: status === 'uploading'
      ? 'rgba(0,0,0,0.5)'
      : status === 'success'
        ? 'rgba(76,175,80,0.35)'
        : 'rgba(211,47,47,0.45)',
    borderRadius: 'inherit',
    zIndex: 2,
  }}>
    {status === 'uploading' && <CircularProgress size={28} sx={{ color: '#fff' }} />}
    {status === 'success' && <CheckCircle sx={{ color: '#fff', fontSize: 32 }} />}
    {status === 'error' && <ErrorOutline sx={{ color: '#fff', fontSize: 32 }} />}
  </Box>
);

const ImageUpload: React.FC<ImageUploadProps> = ({
  onProfileImageChange,
  onCarouselImagesChange,
  onRemoveProfileImage,
  onRemoveCarouselImage,
  onAutoUploadProfileImage,
  onAutoUploadCarouselImage,
  profileImage,
  carouselImages = [],
}) => {
  const theme = useTheme();
  const profileInputRef = useRef<HTMLInputElement>(null);
  const carouselInputRef = useRef<HTMLInputElement>(null);

  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<'uploading' | 'success' | 'error' | null>(null);
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogImage, setDialogImage] = useState<string>('');

  const existingProfileImage = profileImage || null;
  const existingCarouselImages = carouselImages || [];

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.readAsDataURL(file);
    });

  const handleProfileImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const preview = await readFileAsDataUrl(file);
    setProfilePreview(preview);
    onProfileImageChange(file);

    if (onAutoUploadProfileImage) {
      setProfileStatus('uploading');
      const ok = await onAutoUploadProfileImage(file);
      if (ok) {
        setProfileStatus('success');
        setTimeout(() => {
          setProfilePreview(null);
          setProfileStatus(null);
        }, 800);
      } else {
        setProfileStatus('error');
      }
    }
  };

  const handleCarouselImagesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    // Reset so re-selecting the same file fires onChange again
    event.target.value = '';

    for (const file of files) {
      const id = `${Date.now()}-${Math.random()}`;
      const preview = await readFileAsDataUrl(file);

      const item: CarouselItem = { id, preview, file, status: 'uploading' };
      setCarouselItems(prev => [...prev, item]);

      if (onAutoUploadCarouselImage) {
        const ok = await onAutoUploadCarouselImage(file);
        setCarouselItems(prev => {
          const updated = prev.map(i => i.id === id ? { ...i, status: ok ? 'success' as const : 'error' as const } : i);
          if (ok) {
            // remove after brief success flash — server image now in existingCarouselImages prop
            setTimeout(() => setCarouselItems(p => p.filter(i => i.id !== id)), 600);
          }
          return updated;
        });
      } else {
        // No auto-upload: mark success and accumulate for form submit
        setCarouselItems(prev => prev.map(i => i.id === id ? { ...i, status: 'success' } : i));
        onCarouselImagesChange(carouselItems.filter(i => i.status !== 'error').map(i => i.file).concat(file));
      }
    }
  };

  const retryCarouselUpload = async (item: CarouselItem) => {
    if (!onAutoUploadCarouselImage) return;
    setCarouselItems(prev => prev.map(i => i.id === item.id ? { ...i, status: 'uploading' } : i));
    const ok = await onAutoUploadCarouselImage(item.file);
    setCarouselItems(prev => {
      const updated = prev.map(i => i.id === item.id ? { ...i, status: ok ? 'success' as const : 'error' as const } : i);
      if (ok) {
        setTimeout(() => setCarouselItems(p => p.filter(i => i.id !== item.id)), 600);
      }
      return updated;
    });
  };

  const removeProfilePreview = () => {
    setProfilePreview(null);
    setProfileStatus(null);
    onProfileImageChange(null);
    if (profileInputRef.current) profileInputRef.current.value = '';
  };

  const removeCarouselItem = (id: string) => {
    setCarouselItems(prev => {
      const updated = prev.filter(i => i.id !== id);
      onCarouselImagesChange(updated.map(i => i.file));
      return updated;
    });
  };

  const removeExistingProfileImage = () => onRemoveProfileImage?.();

  const removeExistingCarouselImage = (index: number) => {
    const imageUrl = existingCarouselImages[index];
    if (onRemoveCarouselImage && imageUrl) onRemoveCarouselImage(imageUrl);
  };

  const openImageDialog = (image: string) => {
    setDialogImage(image);
    setDialogOpen(true);
  };

  const anyUploading = profileStatus === 'uploading' || carouselItems.some(i => i.status === 'uploading');

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        Store Images
      </Typography>

      {/* Profile Image */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight={500}>
          Profile Image
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <input
            ref={profileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleProfileImageChange}
          />

          {existingProfileImage && (
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
              <Card sx={{ width: 120, height: 'auto', position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="120"
                  image={existingProfileImage}
                  alt="Current Profile"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openImageDialog(existingProfileImage)}
                />
                <CardActions sx={{ p: 0.5, justifyContent: 'center' }}>
                  <IconButton size="small" onClick={removeExistingProfileImage} color="error">
                    <Delete />
                  </IconButton>
                </CardActions>
                <Box sx={{ position: 'absolute', top: 4, left: 4, backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', px: 1, py: 0.5, borderRadius: 1, fontSize: '0.7rem' }}>
                  Current
                </Box>
              </Card>
            </motion.div>
          )}

          {profilePreview && (
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
              <Card sx={{ width: 120, height: 'auto', position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="120"
                  image={profilePreview}
                  alt="New Profile"
                  sx={{ cursor: 'pointer', opacity: profileStatus === 'uploading' ? 0.6 : 1 }}
                  onClick={() => !profileStatus && openImageDialog(profilePreview)}
                />
                {profileStatus && <StatusOverlay status={profileStatus} />}
                {!profileStatus && (
                  <CardActions sx={{ p: 0.5, justifyContent: 'center' }}>
                    <IconButton size="small" onClick={removeProfilePreview} color="error">
                      <Delete />
                    </IconButton>
                  </CardActions>
                )}
                {profileStatus === 'error' && (
                  <CardActions sx={{ p: 0.5, justifyContent: 'center' }}>
                    <IconButton size="small" onClick={() => profileInputRef.current?.click()} color="warning" title="Retry">
                      <Refresh />
                    </IconButton>
                    <IconButton size="small" onClick={removeProfilePreview} color="error">
                      <Delete />
                    </IconButton>
                  </CardActions>
                )}
                <Box sx={{ position: 'absolute', top: 4, left: 4, backgroundColor: 'rgba(76,175,80,0.9)', color: 'white', px: 1, py: 0.5, borderRadius: 1, fontSize: '0.7rem', zIndex: 3 }}>
                  {profileStatus === 'uploading' ? 'Uploading…' : profileStatus === 'error' ? 'Failed' : 'New'}
                </Box>
              </Card>
            </motion.div>
          )}

          {!profilePreview && !existingProfileImage && (
            <Box
              sx={{ width: 120, height: 120, border: `2px dashed ${theme.palette.divider}`, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.action.hover } }}
              onClick={() => profileInputRef.current?.click()}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PhotoCamera sx={{ fontSize: 40, color: theme.palette.text.secondary, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">Upload Profile</Typography>
              </Box>
            </Box>
          )}

          <Box>
            <Button
              variant="outlined"
              startIcon={profileStatus === 'uploading' ? <CircularProgress size={16} /> : <CloudUpload />}
              onClick={() => profileInputRef.current?.click()}
              disabled={profileStatus === 'uploading'}
              sx={{ mb: 1 }}
            >
              {profilePreview ? 'Change' : existingProfileImage ? 'Replace' : 'Upload'} Profile Image
            </Button>
            <Typography variant="caption" display="block" color="text.secondary">
              Recommended: 400×400px, max 5MB
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Carousel Images */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight={500}>
          Carousel Images ({existingCarouselImages.length + carouselItems.length})
        </Typography>

        <input
          ref={carouselInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleCarouselImagesChange}
        />

        <Grid container spacing={2}>
          {existingCarouselImages.map((image, index) => (
            <Grid item xs={6} sm={4} md={3} key={`existing-${index}`}>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
                <Card sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="120"
                    image={image}
                    alt={`Carousel ${index + 1}`}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openImageDialog(image)}
                  />
                  <CardActions sx={{ p: 0.5, justifyContent: 'center' }}>
                    <IconButton size="small" onClick={() => removeExistingCarouselImage(index)} color="error">
                      <Delete />
                    </IconButton>
                  </CardActions>
                  <Box sx={{ position: 'absolute', top: 4, left: 4, backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', px: 1, py: 0.5, borderRadius: 1, fontSize: '0.7rem' }}>
                    Current
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}

          {carouselItems.map((item) => (
            <Grid item xs={6} sm={4} md={3} key={item.id}>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}>
                <Card sx={{ position: 'relative' }}>
                  <Box sx={{ position: 'relative', height: 120 }}>
                    <CardMedia
                      component="img"
                      height="120"
                      image={item.preview}
                      alt="Uploading"
                      sx={{ cursor: item.status === 'error' ? 'default' : 'pointer', opacity: item.status === 'uploading' ? 0.5 : 1 }}
                      onClick={() => item.status !== 'uploading' && openImageDialog(item.preview)}
                    />
                    <StatusOverlay status={item.status} />
                  </Box>
                  <CardActions sx={{ p: 0.5, justifyContent: 'center' }}>
                    {item.status === 'error' && (
                      <IconButton size="small" onClick={() => retryCarouselUpload(item)} color="warning" title="Retry">
                        <Refresh />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => removeCarouselItem(item.id)} color="error" disabled={item.status === 'uploading'}>
                      <Delete />
                    </IconButton>
                  </CardActions>
                  <Box sx={{ position: 'absolute', top: 4, left: 4, backgroundColor: item.status === 'error' ? 'rgba(211,47,47,0.85)' : 'rgba(76,175,80,0.9)', color: 'white', px: 1, py: 0.5, borderRadius: 1, fontSize: '0.7rem', zIndex: 3 }}>
                    {item.status === 'uploading' ? 'Uploading…' : item.status === 'error' ? 'Failed' : 'Uploaded'}
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}

          <Grid item xs={6} sm={4} md={3}>
            <Box
              sx={{ height: 120, border: `2px dashed ${theme.palette.divider}`, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: anyUploading ? 'not-allowed' : 'pointer', opacity: anyUploading ? 0.5 : 1, '&:hover': !anyUploading ? { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.action.hover } : {} }}
              onClick={() => !anyUploading && carouselInputRef.current?.click()}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Add sx={{ fontSize: 40, color: theme.palette.text.secondary, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">Add Image</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={anyUploading ? <CircularProgress size={16} /> : <CloudUpload />}
            onClick={() => carouselInputRef.current?.click()}
            disabled={anyUploading}
          >
            {anyUploading ? 'Uploading…' : 'Upload Carousel Images'}
          </Button>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
            Recommended: 800×400px, max 5MB each
          </Typography>
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Image Preview
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <img src={dialogImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageUpload;
